/**
 * Terminal layout store — persists flexlayout-react model to localStorage.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type LayoutPreset = "default" | "monitoring" | "trading";

export interface TerminalStore {
  /** flexlayout-react model JSON (serialized) */
  layoutJson: Record<string, unknown> | null;
  /** Active layout preset name */
  activePreset: LayoutPreset;
  /** Selected agent for detail panels */
  selectedAgentId: string | null;
  /** Whether the terminal is in compact mode */
  compact: boolean;

  setLayout: (json: Record<string, unknown>) => void;
  setActivePreset: (preset: LayoutPreset) => void;
  selectAgent: (agentId: string | null) => void;
  setCompact: (compact: boolean) => void;
  resetLayout: () => void;
}

type PersistedTerminalState = Pick<
  TerminalStore,
  "layoutJson" | "activePreset" | "compact"
>;

export const useTerminalStore = create<TerminalStore>()(
  persist(
    (set) => ({
      layoutJson: null,
      activePreset: "default",
      selectedAgentId: null,
      compact: false,

      setLayout: (json) => set({ layoutJson: json }),
      setActivePreset: (preset) =>
        set({ activePreset: preset, layoutJson: null }),
      selectAgent: (agentId) => set({ selectedAgentId: agentId }),
      setCompact: (compact) => set({ compact }),
      resetLayout: () => set({ layoutJson: null, activePreset: "default" }),
    }),
    {
      name: "nnt-terminal-layout",
      // Version stamp so a future flexlayout-react schema change can
      // invalidate stale persisted layouts deliberately (bump + migrate)
      // instead of relying on the Model.fromJson recovery catch.
      version: 1,
      // v0 (pre-versioning) layouts are structurally compatible — keep them.
      migrate: (persisted) => persisted as PersistedTerminalState,
      partialize: (state): PersistedTerminalState => ({
        layoutJson: state.layoutJson,
        activePreset: state.activePreset,
        compact: state.compact,
      }),
    },
  ),
);
